import React from "react";
import {
  PanelRow,
  TextInput,
  Label,
  Radio,
  RadioGroup,
  Dropdown,
  DropdownOption,
  CheckBox,
  TextInputMultiline,
  DatePicker,
} from "@backslashbuild/pp-ui";
import { utcToYmd, textToBoolean } from "./utils";

export default function FormField({ field, onSave }) {
  let SubComponent = TextField;

  if (field.schema.type === "date") SubComponent = DateField;
  if (field.schema.type === "radio") SubComponent = RadioField;
  if (field.schema.type === "dropdown") SubComponent = DropdownField;
  if (field.schema.type === "checkbox") SubComponent = CheckboxField;
  if (field.schema.type === "textbox") SubComponent = TextBoxField;

  return (
    <PanelRow>
      <Label label={field.displayName}>
        <SubComponent field={field} onSave={onSave} />
      </Label>
    </PanelRow>
  );
}

function TextField({ field, onSave }) {
  const [value, setValue] = React.useState(field.value);
  const [isDisabled, setIsDisabled] = React.useState(field.isDisabled);
  const prefix = field.schema.type === "currency" ? "£" : "";
  const suffix = field.schema.type === "percentage" ? "%" : "";

  React.useEffect(() => {
    setValue(field.value);
  }, [field.value]);

  React.useEffect(() => {
    setIsDisabled(field.isDisabled);
  }, [field.isDisabled]);

  return (
    <TextInput
      value={value}
      prefix={prefix}
      suffix={suffix}
      disabled={isDisabled}
      onChange={(e) => {
        setValue(e.target.value);
      }}
      onBlur={() => {
        onSave(value);
      }}
    ></TextInput>
  );
}

function RadioField({ field, onSave }) {
  const [value, setValue] = React.useState(field.value);

  React.useEffect(() => {
    setValue(field.value);
  }, [field.value]);

  return (
    <RadioGroup
      value={value}
      onChange={(e) => {
        onSave(e.target.value);
        setValue(e.target.value);
      }}
    >
      {field.schema.options.map((option) => (
        <Radio value={option.value} label={option.displayName} />
      ))}
    </RadioGroup>
  );
}

function DropdownField({ field, onSave }) {
  const [value, setValue] = React.useState(field.value);

  React.useEffect(() => {
    setValue(field.value);
  }, [field.value]);

  return (
    <Dropdown
      value={value}
      onChange={(e) => {
        onSave(e.target.value);
        setValue(e.target.value);
      }}
    >
      <DropdownOption value="">Choose:</DropdownOption>
      {field.schema.options.map((option) => (
        <DropdownOption value={option.value}>{option.displayName}</DropdownOption>
      ))}
    </Dropdown>
  );
}

function CheckboxField({ field, onSave }) {
  const [value, setValue] = React.useState(textToBoolean(field.value));
  const label = field.schema.options[0].displayName;

  React.useEffect(() => {
    setValue(textToBoolean(field.value));
  }, [field.value]);

  return (
    <CheckBox
      checked={value}
      label={label}
      onChange={(e) => {
        onSave(String(e.target.checked));
        setValue(e.target.checked);
      }}
    ></CheckBox>
  );
}

function DateField({ field, onSave }) {
  const [value, setValue] = React.useState(utcToYmd(field.value));
  const [isDisabled, setIsDisabled] = React.useState(field.isDisabled);

  React.useEffect(() => {
    setValue(utcToYmd(field.value));
  }, [field.value]);

  React.useEffect(() => {
    setIsDisabled(field.isDisabled);
  }, [field.isDisabled]);

  return (
    <DatePicker
      value={value}
      disabled={isDisabled}
      onChange={(v) => {
        setValue(utcToYmd(v));
        onSave(v?.isValid ? utcToYmd(v) : "");
      }}
    ></DatePicker>
  );
}

function TextBoxField({ field, onSave }) {
  const [value, setValue] = React.useState(field.value);
  const [isDisabled, setIsDisabled] = React.useState(field.isDisabled);

  React.useEffect(() => {
    setValue(field.value);
  }, [field.value]);

  React.useEffect(() => {
    setIsDisabled(field.isDisabled);
  }, [field.isDisabled]);

  return (
    <TextInputMultiline
      value={value}
      disabled={isDisabled}
      onChange={(e) => {
        setValue(e.target.value);
      }}
      onBlur={() => {
        onSave(value);
      }}
      rows={10}
    ></TextInputMultiline>
  );
}
